import { useCallback, useEffect, useState } from 'react';
import { Activity, Loader2 } from 'lucide-react';
import { spaceErrorMessage, spacesApi, type SpaceActivityRow } from '../../lib/api';
import { formatBytes } from '../../lib/spaces';
import { formatDate, timeAgo } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const LIMIT = 50;

const dateOnly = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

function text(detail: SpaceActivityRow['detail'], key: string): string | null {
  const value = detail?.[key];
  return typeof value === 'string' && value.trim() ? value : null;
}

function quoted(name: string | null, kind: 'folder' | 'file'): string {
  return name ? `${kind} “${name}”` : `a ${kind}`;
}

/**
 * One activity row as a sentence, from what the service recorded in `detail`.
 *
 * Each action records different fields (a rename its old and new name, a grant the person and access,
 * a move where it went), so every sentence falls back to a plain one when a field is missing rather
 * than printing "undefined" — and an action this page does not know yet still prints its own key.
 */
export function describeActivity(row: SpaceActivityRow): string {
  const d = row.detail;
  const name = text(d, 'name');
  const from = text(d, 'from');
  const to = text(d, 'to');
  const email = text(d, 'email') ?? 'someone';
  const access = text(d, 'access');
  const into = text(d, 'into');
  const folder = text(d, 'folder');
  const sharedFolder = folder ? `folder “${folder}”` : 'a folder';
  const size = typeof d?.sizeBytes === 'number' ? ` (${formatBytes(d.sizeBytes)})` : '';

  switch (row.action) {
    case 'space.created':
      return 'created this space';
    case 'folder.created':
      return `created ${quoted(name, 'folder')}`;
    case 'folder.renamed':
      return from && to ? `renamed folder “${from}” to “${to}”` : 'renamed a folder';
    case 'folder.moved':
      return `moved ${quoted(name, 'folder')}${into ? ` into “${into}”` : ''}`;
    case 'folder.trashed':
      return `moved ${quoted(name, 'folder')} to the trash`;
    case 'folder.restored':
      return `restored ${quoted(name, 'folder')} from the trash`;
    case 'file.uploaded':
      return d?.replaced ? `uploaded a new version of ${quoted(name, 'file')}${size}` : `uploaded ${quoted(name, 'file')}${size}`;
    case 'file.written':
      return `edited ${quoted(name, 'file')}${size}`;
    case 'file.renamed':
      return from && to ? `renamed file “${from}” to “${to}”` : 'renamed a file';
    case 'file.moved':
      return `moved ${quoted(name, 'file')}${into ? ` into “${into}”` : ''}`;
    case 'file.trashed':
      return `moved ${quoted(name, 'file')} to the trash`;
    case 'file.restored':
      return `restored ${quoted(name, 'file')} from the trash`;
    case 'grant.added': {
      const until = text(d, 'expiresAt');
      return `shared ${sharedFolder} with ${email}${access ? ` as ${access}` : ''}${until ? ` until ${dateOnly.format(new Date(until))}` : ''}`;
    }
    case 'grant.changed': {
      const previous = text(d, 'previousAccess');
      return `changed ${email}'s access to ${sharedFolder}${previous ? ` from ${previous}` : ''}${access ? ` to ${access}` : ''}`;
    }
    case 'grant.removed':
      return `removed ${email}'s access to ${sharedFolder}`;
    case 'share.requested':
      return access === 'none'
        ? `asked to take ${email}'s access to ${sharedFolder} away`
        : `asked to share ${sharedFolder} with ${email}${access ? ` as ${access}` : ''}`;
    case 'share.approved':
      return `approved the request about ${email} and ${sharedFolder}`;
    case 'share.declined':
      return `declined the request about ${email} and ${sharedFolder}`;
    default:
      return row.action;
  }
}

/**
 * What happened in your space, newest first: your own changes, your agents' through MCP, and what
 * people you shared with did inside your folders.
 */
export function ActivityView({
  myUserId, onOpenFolder, onOpenFile,
}: {
  myUserId: string | null;
  onOpenFolder: (folderId: string) => void;
  onOpenFile: (folderId: string, fileId: string) => void;
}) {
  const [rows, setRows] = useState<SpaceActivityRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(async (before?: string) => {
    try {
      const page = await spacesApi.activity({ before, limit: LIMIT });
      // By id: rows sharing the boundary timestamp could otherwise appear on both pages.
      setRows((prev) => {
        if (!before || !prev) return page;
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...page.filter((r) => !seen.has(r.id))];
      });
      setMore(page.length >= LIMIT);
      setError(null);
    } catch (err) {
      setError(spaceErrorMessage(err, 'Could not load activity'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function loadMore() {
    const last = rows?.[rows.length - 1];
    if (!last) return;
    setLoadingMore(true);
    await load(last.createdAt);
    setLoadingMore(false);
  }

  return (
    <div>
      <h1 className="text-lg font-bold text-ink flex items-center gap-2">
        <Activity size={16} className="text-ink-faint" />
        Activity
      </h1>
      <p className="mt-1 mb-5 max-w-2xl text-xs text-ink-muted">
        Everything done in your space — by you in the console, by your MCP client, and by the people you shared folders with.
      </p>

      {error && <div className="mb-4 rounded-md border border-danger-ring bg-danger-bg px-4 py-3 text-sm text-danger-text">{error}</div>}

      {rows === null ? (
        <p className="text-xs text-ink-faint">{error ? '' : 'Loading…'}</p>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-border bg-card py-16 text-center text-ink-faint">
          <Activity size={20} className="mx-auto mb-2 opacity-60" />
          <p className="text-sm">Nothing has happened here yet.</p>
        </div>
      ) : (
        <>
          <ul className="rounded-lg border border-border bg-card divide-y divide-border-subtle">
            {rows.map((row) => {
              const actor = row.actorId === myUserId ? 'You' : row.actorEmail ?? 'Someone';
              const open =
                row.fileId && row.folderId
                  ? { label: 'open file', go: () => onOpenFile(row.folderId!, row.fileId!) }
                  : row.folderId && row.action !== 'space.created'
                    ? { label: 'open folder', go: () => onOpenFolder(row.folderId!) }
                    : null;
              return (
                <li key={row.id} className="flex items-start gap-3 px-4 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-ink-secondary">
                      <span className="font-medium text-ink">{actor}</span> {describeActivity(row)}
                    </p>
                    <p className="mt-0.5 text-[11px] text-ink-faint flex items-center gap-1.5 flex-wrap">
                      <span title={formatDate(row.createdAt)}>{timeAgo(row.createdAt)}</span>
                      {actor === 'You' && row.actorEmail && <span>· {row.actorEmail}</span>}
                      <Badge variant={row.via === 'mcp' ? 'info' : 'muted'} className="px-1.5 py-0 text-[10px]">
                        {row.via === 'mcp' ? 'via MCP' : 'console'}
                      </Badge>
                      {open && (
                        <button type="button" onClick={open.go} className="text-orange-600 dark:text-orange-400 hover:underline">
                          {open.label}
                        </button>
                      )}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
          {more && (
            <div className="mt-3 flex justify-center">
              <Button size="sm" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore && <Loader2 size={12} className="animate-spin" />}
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
